import { countWords } from '../wordCount';
import { normalizeName } from '../resolve';

export const SECTION_WORD_LIMIT = 30000;
const CELL_WORD_LIMIT = 60;

/**
 * Indicator names and table values are not prose, so they sit outside the section budget.
 * Mirrors the exclude list on keyPerformanceIndicators in the API's plan_content.json.
 * Both sides count everything else, so a prose field added to the section starts counting
 * on both at once rather than only server-side.
 */
export const SECTION_COUNT_EXCLUDE = ['name', 'indicatorValues'];

const valueColumn = (header, key) => ({
  header,
  key,
  required: false,
  type: 'string',
  width: 18,
  wordLimit: CELL_WORD_LIMIT,
});

/**
 * Import definition for performance target indicators on the Performance
 * Monitoring Framework page.
 * Target: planStore.content.keyPerformanceIndicators.performanceTargets
 *
 * Indicators reference their development strategy by name; the strategy must
 * already exist. Year 5 has no actual column because the plan document never
 * captures one.
 */
export const indicatorsImport = {
  type: 'indicators',
  sheetName: 'Indicators',
  templateFileName: 'indicators-template.xlsx',
  noun: { singular: 'indicator', plural: 'indicators' },
  replaceWarning: 'Importing will replace all existing indicators for every development strategy in this plan, including any that were added manually.',

  columns: [
    { header: 'Development Strategy', key: 'strategy', required: true, type: 'string', width: 32, link: 'developmentStrategy' },
    { header: 'Indicator Name', key: 'name', required: true, type: 'string', width: 40 },
    { header: 'Description', key: 'description', required: false, type: 'text', width: 64 },
    valueColumn('Baseline', 'baseline'),
    valueColumn('Year 1 Target', 'year1Target'),
    valueColumn('Year 1 Actual', 'year1Actual'),
    valueColumn('Year 2 Target', 'year2Target'),
    valueColumn('Year 2 Actual', 'year2Actual'),
    valueColumn('Year 3 Target', 'year3Target'),
    valueColumn('Year 3 Actual', 'year3Actual'),
    valueColumn('Year 4 Target', 'year4Target'),
    valueColumn('Year 4 Actual', 'year4Actual'),
    valueColumn('Year 5 Target', 'year5Target'),
  ],

  previewColumns: [
    { key: 'strategy', label: 'Development Strategy' },
    { key: 'name', label: 'Indicator Name' },
    { key: 'baseline', label: 'Baseline' },
  ],

  buildContext: (planStore) => ({
    developmentStrategy: {
      noun: 'development strategy',
      candidates: planStore.content.developmentStrategies
        .map(strategy => ({ id: strategy.id, name: strategy.name })),
    },
  }),

  validateRows: (rows, addError) => {
    const firstRowUsedAt = new Map();

    rows.forEach(({ excelRow, values, links }) => {
      if (!values.name || !links.strategy) return;

      const key = `${links.strategy.id}|${normalizeName(values.name)}`;

      if (firstRowUsedAt.has(key)) {
        addError(excelRow, 'Indicator Name', `Duplicate indicator "${values.name}" for strategy "${links.strategy.name}" — first used on row ${firstRowUsedAt.get(key)}.`);
      } else {
        firstRowUsedAt.set(key, excelRow);
      }
    });
  },

  buildResult: (rows, context, nextId) => {
    const strategyIds = new Set();

    const items = rows.map(({ values, links }) => {
      strategyIds.add(links.strategy.id);

      return {
        id: nextId(),
        developmentStrategyId: links.strategy.id,
        name: values.name,
        description: values.description,
        indicatorValues: {
          baseline: values.baseline,
          year1: { target: values.year1Target, actual: values.year1Actual },
          year2: { target: values.year2Target, actual: values.year2Actual },
          year3: { target: values.year3Target, actual: values.year3Actual },
          year4: { target: values.year4Target, actual: values.year4Actual },
          year5: { target: values.year5Target, actual: null },
        },
      };
    });

    const preview = rows.map(({ values, links }) => ({
      strategy: links.strategy.name,
      name: values.name,
      baseline: values.baseline,
    }));

    return { items, preview, strategyCount: strategyIds.size };
  },

  validateSectionLimit: (result, planStore) => {
    // The import replaces every indicator, so the budget is measured against the post-import section
    const wouldBeSection = { ...planStore.content.keyPerformanceIndicators, performanceTargets: result.items };
    const words = countWords(wouldBeSection, SECTION_COUNT_EXCLUDE);

    if (words > SECTION_WORD_LIMIT) {
      return `This import would put the Performance Monitoring Framework section over its ${SECTION_WORD_LIMIT}-word limit (${words} words). Reduce the content and try again.`;
    }

    return null;
  },

  previewRows: (result) => result.preview,

  summarize: (result) => {
    const indicators = `${result.items.length} ${result.items.length === 1 ? 'indicator' : 'indicators'}`;
    const strategies = `${result.strategyCount} ${result.strategyCount === 1 ? 'development strategy' : 'development strategies'}`;

    return `${indicators} across ${strategies} ready to import`;
  },

  commit: (planStore, result) => {
    planStore.content.keyPerformanceIndicators.performanceTargets = result.items;
  },
};
